// ============================================================================
// LineChart — a vivid, animated inline-SVG area/line chart.
// ----------------------------------------------------------------------------
// Pure SVG (no charting dependency), mirroring the locked dashboard
// ProgressSnapshot pattern: animated path-draw, gradient area fill, a pulsing
// end dot, and an optional dashed threshold reference line. Reduced-motion
// aware (paths snap to final, no draw). Responsive via viewBox + w-full.
// ============================================================================
import { useId, useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import type { SeriesPoint } from "../../../api";
import { fmtAxisDate, fmtNum } from "../format";

export type ChartAccent = "coral" | "violet" | "ocean" | "lime";

const ACCENTS: Record<ChartAccent, { from: string; to: string }> = {
  coral: { from: "#FF5E62", to: "#FF9966" },
  violet: { from: "#6A5BFF", to: "#9D6BFF" },
  ocean: { from: "#2D8FFF", to: "#5B6BFF" },
  lime: { from: "#18C964", to: "#3BE085" },
};

const W = 560;
const H = 220;
const PAD = { top: 18, right: 18, bottom: 30, left: 40 };

export interface LineChartProps {
  series: SeriesPoint[];
  accent?: ChartAccent;
  /** Y decimals for the axis ticks + value labels. */
  decimals?: number;
  /** Optional dashed reference line (e.g. a ratio risk threshold). */
  threshold?: number;
  thresholdLabel?: string;
  /** Accessible label for the chart. */
  ariaLabel?: string;
  /** BCP-47 locale for tick/number formatting. */
  locale?: string;
}

/** A small line/area trend chart for a single measurement series. */
export function LineChart({
  series,
  accent = "coral",
  decimals = 1,
  threshold,
  thresholdLabel,
  ariaLabel,
  locale,
}: LineChartProps) {
  const reduce = useReducedMotion();
  const uid = useId();
  const colors = ACCENTS[accent];

  const chart = useMemo(() => {
    if (series.length < 2) return null;
    const ys = series.map((p) => p.y);
    const candidates = [...ys];
    if (threshold !== undefined) candidates.push(threshold);
    const rawLo = Math.min(...candidates);
    const rawHi = Math.max(...candidates);
    const span = rawHi - rawLo || Math.max(Math.abs(rawHi), 1);
    const padY = span * 0.12;
    const lo = rawLo - padY;
    const hi = rawHi + padY;
    const innerW = W - PAD.left - PAD.right;
    const innerH = H - PAD.top - PAD.bottom;
    const range = hi - lo || 1;

    const x = (i: number) => PAD.left + (innerW * i) / (series.length - 1);
    const y = (v: number) => PAD.top + innerH * (1 - (v - lo) / range);

    const linePath = series
      .map((p, i) => `${i === 0 ? "M" : "L"} ${x(i).toFixed(1)} ${y(p.y).toFixed(1)}`)
      .join(" ");
    const areaPath =
      `${linePath} L ${x(series.length - 1).toFixed(1)} ${(H - PAD.bottom).toFixed(1)}` +
      ` L ${x(0).toFixed(1)} ${(H - PAD.bottom).toFixed(1)} Z`;

    const ticks = [hi, (lo + hi) / 2, lo];

    return {
      x,
      y,
      linePath,
      areaPath,
      ticks,
      lastX: x(series.length - 1),
      lastY: y(ys[ys.length - 1]),
      firstX: x(0),
      lastIdx: series.length - 1,
    };
  }, [series, threshold]);

  if (!chart) return null;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="block w-full"
      role="img"
      aria-label={ariaLabel}
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <linearGradient id={`${uid}-area`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={colors.from} stopOpacity="0.26" />
          <stop offset="100%" stopColor={colors.to} stopOpacity="0" />
        </linearGradient>
        <linearGradient id={`${uid}-line`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={colors.from} />
          <stop offset="100%" stopColor={colors.to} />
        </linearGradient>
      </defs>

      {/* Y gridlines + ticks */}
      {chart.ticks.map((tick, i) => (
        <g key={`${tick}-${i}`}>
          <line
            x1={PAD.left}
            x2={W - PAD.right}
            y1={chart.y(tick)}
            y2={chart.y(tick)}
            stroke="#EAEDF5"
            strokeWidth="1"
          />
          <text
            x={PAD.left - 8}
            y={chart.y(tick) + 4}
            textAnchor="end"
            className="fill-body-faint text-[10px] font-bold"
          >
            {fmtNum(tick, decimals, locale)}
          </text>
        </g>
      ))}

      {/* Optional dashed threshold reference line */}
      {threshold !== undefined && (
        <g>
          <line
            x1={PAD.left}
            x2={W - PAD.right}
            y1={chart.y(threshold)}
            y2={chart.y(threshold)}
            stroke="#F0484C"
            strokeWidth="1.5"
            strokeDasharray="5 5"
            opacity="0.7"
          />
          {thresholdLabel && (
            <text
              x={W - PAD.right}
              y={chart.y(threshold) - 6}
              textAnchor="end"
              className="fill-volt-600 text-[10px] font-bold"
            >
              {thresholdLabel}
            </text>
          )}
        </g>
      )}

      {/* X axis labels: first + last */}
      <text
        x={chart.firstX}
        y={H - 8}
        textAnchor="start"
        className="fill-body-faint text-[10px] font-bold"
      >
        {fmtAxisDate(series[0].x, locale)}
      </text>
      <text
        x={chart.lastX}
        y={H - 8}
        textAnchor="end"
        className="fill-body-faint text-[10px] font-bold"
      >
        {fmtAxisDate(series[chart.lastIdx].x, locale)}
      </text>

      <motion.path
        d={chart.areaPath}
        fill={`url(#${uid}-area)`}
        initial={reduce ? false : { opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.6, delay: reduce ? 0 : 0.3 }}
      />
      <motion.path
        d={chart.linePath}
        fill="none"
        stroke={`url(#${uid}-line)`}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={reduce ? false : { pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 1.1, ease: [0.22, 0.61, 0.36, 1] }}
      />
      <motion.circle
        cx={chart.lastX}
        cy={chart.lastY}
        r="5"
        fill="#fff"
        stroke={colors.from}
        strokeWidth="3"
        initial={reduce ? false : { scale: 0 }}
        whileInView={{ scale: 1 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{
          delay: reduce ? 0 : 1.05,
          type: "spring",
          stiffness: 400,
          damping: 18,
        }}
        style={
          { transformOrigin: "center", transformBox: "fill-box" } as React.CSSProperties
        }
      />
    </svg>
  );
}
